import React from 'react'

import { useTheme, Typography, Box, InfoCircle, CloseIcon, Chip } from '@hermes/web-components'

import useTranslateMessage from '@hooks/useTranslateMessage'
import SortModal from '@components/SortModal'
import FilterModal from '@components/FilterModal'
import useRecommendedSellerInfoModal from '@hooks/useRecommendedSellerInfo'

import { ModalTypes, SortData, FilterData, ConditionFilterTypes } from './types'

import {
  SortBody,
  SortValueContainer,
  SortValue,
  SortOrFilterButton,
  SellersTitleContainer,
  SellersTitle
} from './SortAndFilterBar.styles'

interface Props {
  filterData: FilterData
  sortData: SortData
  filterModalOpen: boolean
  sortModalOpen: boolean
  chipClicked: (type: keyof typeof ConditionFilterTypes) => void
  handleFilterSubmit: (data: FilterData) => void
  handleSortSubmit: (data: SortData) => void
  toggleModalOpen: (type: ModalTypes) => void
}

const SortAndFilterBar = ({
  filterData,
  sortData,
  filterModalOpen,
  sortModalOpen,
  chipClicked,
  handleFilterSubmit,
  handleSortSubmit,
  toggleModalOpen
}: Props) => {
  const theme = useTheme()
  const { t } = useTranslateMessage()
  const { RecommendedSellerInfoModal, toggleModalOpen: toggleInfoModalOpen } = useRecommendedSellerInfoModal()

  const currentDeliveryOption = filterData.DELIVERY_METHOD.options.find(({ active }) => active)
  const currentSortingOption = sortData.find(({ active }) => active)
  const activeConditions = filterData.CONDITION.options.filter(({ active }) => active)
  const activeConditionValues = activeConditions.map(({ value }) => value)
  const filterCount = activeConditionValues.length

  return (
    <>
      <SellersTitleContainer>
        <SellersTitle variant="h1">
          {currentDeliveryOption?.type === 'DELIVERY_METHOD_MEETUP'
            ? t({ id: 'purchase_flow.meetup_listings_title', defaultMessage: 'Meetup' })
            : t({ id: 'purchase_flow.delivery_listings_title', defaultMessage: 'Home to home delivery' })}
        </SellersTitle>
      </SellersTitleContainer>

      <Box mb="0" display="flex" flexWrap="wrap" gap="6px" alignItems="center" justifyContent="space-between">
        <SortBody>
          <SortOrFilterButton variant="ghost" position="left" onClick={() => toggleModalOpen(ModalTypes.SORT)}>
            {`${t({ id: 'common.sorted_by', defaultMessage: 'Sorted by' })}:`}
          </SortOrFilterButton>
          <SortValueContainer>
            <SortValue>{currentSortingOption?.label}</SortValue>
            {currentSortingOption?.type === 'SCORE' && (
              <Box ml={'6px'} style={{ display: 'flex' }}>
                <InfoCircle
                  onClick={toggleInfoModalOpen}
                  width="12px"
                  height="12px"
                  fill={theme.palette.neutral.main}
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            )}
          </SortValueContainer>
        </SortBody>
        <SortOrFilterButton variant="ghost" position="right" onClick={() => toggleModalOpen(ModalTypes.FILTER)}>
          {t({ id: 'common.filter', defaultMessage: 'Filter' })}
          {filterCount > 0 ? ` (${filterCount})` : ''}
        </SortOrFilterButton>
      </Box>

      {activeConditions.length > 0 && (
        <Box display={'flex'} flexDirection={'row-reverse'} style={{ marginTop: '15px', overflow: 'auto' }}>
          {activeConditions.map(({ value, type, label }) => (
            <Chip
              key={value}
              label={
                <Typography display={'flex'} alignItems={'center'} color={theme.palette.neutral[900]} fontWeight={400}>
                  <CloseIcon
                    width="12px"
                    height="12px"
                    fill={theme.palette.neutral[900]}
                    style={{ marginRight: '5px' }}
                  />
                  {label}
                </Typography>
              }
              onClick={() => chipClicked(type)}
              color="default"
              variant="filled"
              style={{ marginLeft: '8px' }}
            />
          ))}
        </Box>
      )}

      <FilterModal
        title={t({ id: 'common.filter', defaultMessage: 'Filter' })}
        isOpen={filterModalOpen}
        data={filterData}
        onSubmit={handleFilterSubmit}
        onClose={() => toggleModalOpen(ModalTypes.FILTER)}
      />
      <SortModal
        title={t({ id: 'common.sort_by', defaultMessage: 'Sort by' })}
        data={sortData}
        isOpen={sortModalOpen}
        onSubmit={handleSortSubmit}
        onClose={() => toggleModalOpen(ModalTypes.SORT)}
      />
      <RecommendedSellerInfoModal />
    </>
  )
}

export default SortAndFilterBar
